/**
 * claw-set-upgrade-auto-v2.js — PvA-FV-20260616-020
 * Set Upgrade Block: dynamic bundle matching via Storefront API + <details> tiles
 * Features: expandable tiles, smooth close, ATC with jQuery/fetch fallback
 */
(async function() {
  'use strict';

  const currentHandle = window.Shopify?.product?.handle
    || window.location.pathname.match(/\/products\/([^\/\?#]+)/)?.[1];
  if (!currentHandle) return;

  // ── Helper: format price (Shopify returns price in cents) ──────────────────
  function formatPrice(priceStr) {
    const cents = parseFloat(priceStr);
    if (isNaN(cents)) return priceStr;
    return (cents / 100).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
  }

  // ── Helper: build a single <details> tile HTML ─────────────────────────────
  function buildTileHTML(bundle) {
    const variantId = bundle.variants?.edges?.[0]?.node?.id?.replace('gid://shopify/ProductVariant/', '') || '';
    const price = bundle.variants?.edges?.[0]?.node?.price || '';
    const imgUrl = bundle.featured_image?.url ? bundle.featured_image.url + '&width=120' : '';
    const imgHTML = imgUrl
      ? `<div class="claw-set-upgrade-tile__thumb"><img src="${imgUrl}" alt="${bundle.title}" width="120" height="120" loading="lazy"></div>`
      : '';
    return `
      <details class="claw-set-upgrade-tile" data-variant-id="${variantId}" data-product-handle="${bundle.handle}">
        <summary class="claw-set-upgrade-tile__summary">
          ${imgHTML}
          <div class="claw-set-upgrade-tile__info">
            <span class="claw-set-upgrade-tile__name">${bundle.title}</span>
            <span class="claw-set-upgrade-tile__price">${formatPrice(price)}</span>
          </div>
          <span class="claw-set-upgrade-tile__chevron" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </span>
        </summary>
        <div class="claw-set-upgrade-tile__body">
          <div class="claw-set-upgrade-tile__body-inner">
            <button class="claw-set-upgrade-tile__atc" type="button" data-variant-id="${variantId}">
              Voeg toe aan winkelwagen
            </button>
          </div>
        </div>
      </details>`;
  }

  // ── Smooth close for <details> tiles ──────────────────────────────────────
  function initSmoothClose(container) {
    container.querySelectorAll('.claw-set-upgrade-tile').forEach(function(details) {
      details.addEventListener('click', function(e) {
        // Only intercept when already open (= closing action)
        if (!details.open) return;
        // If the click target is inside .body (e.g. ATC button), don't close
        if (e.target.closest('.claw-set-upgrade-tile__body')) return;

        e.preventDefault();
        const body = details.querySelector('.claw-set-upgrade-tile__body');
        if (!body) { details.removeAttribute('open'); return; }

        // Force close via inline style override
        body.style.gridTemplateRows = '0fr';
        details.querySelector('.claw-set-upgrade-tile__thumb') && (
          details.querySelector('.claw-set-upgrade-tile__thumb').style.transition = 'width .3s ease, height .3s ease'
        );

        body.addEventListener('transitionend', function handler() {
          body.removeEventListener('transitionend', handler);
          details.removeAttribute('open');
          body.style.gridTemplateRows = ''; // reset — CSS takes over
        }, { once: true });
      });
    });
  }

  // ── ATC handler ───────────────────────────────────────────────────────────
  function initATC(container) {
    container.querySelectorAll('.claw-set-upgrade-tile__atc').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.stopPropagation(); // don't close the details
        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        btn.disabled = true;
        const originalText = btn.textContent;
        btn.textContent = 'Toevoegen...';

        function onSuccess() {
          btn.textContent = '✓ Toegevoegd!';
          // Update cart count
          fetch('/cart.js')
            .then(function(r) { return r.json(); })
            .then(function(cart) {
              document.querySelectorAll('[data-cart-count], .cart-count').forEach(function(el) {
                el.textContent = cart.item_count;
              });
            });
          setTimeout(function() {
            btn.disabled = false;
            btn.textContent = originalText;
          }, 2000);
        }

        function onError() {
          btn.textContent = 'Probeer opnieuw';
          btn.disabled = false;
        }

        // jQuery path
        if (typeof jQuery !== 'undefined') {
          jQuery.ajax({
            type: 'POST',
            url: '/cart/add.js',
            data: { id: variantId, quantity: 1 },
            dataType: 'json',
            success: onSuccess,
            error: onError
          });
        } else {
          // Fetch fallback
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
          })
            .then(function(r) {
              if (!r.ok) throw new Error('ATC failed');
              return r.json();
            })
            .then(onSuccess)
            .catch(onError);
        }
      });
    });
  }

  // ── Fetch bundles via Storefront API ──────────────────────────────────────
  try {
    const response = await fetch('/api/storefront/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: `
          query {
            collectionByHandle(handle: "complete-sets") {
              products(first: 100) {
                edges {
                  node {
                    handle
                    title
                    featured_image: featuredImage { url }
                    variants(first: 1) {
                      edges { node { id price } }
                    }
                    productHandles: metafield(namespace: "custom", key: "product_handles") {
                      value
                    }
                  }
                }
              }
            }
          }
        `
      })
    });

    const data = await response.json();
    const bundles = data.data?.collectionByHandle?.products?.edges || [];

    // Find all bundles containing the current product
    const matchedBundles = bundles
      .map(function(edge) { return edge.node; })
      .filter(function(bundle) {
        const csv = bundle.productHandles?.value || '';
        const handles = csv.split(',').map(function(h) { return h.trim(); });
        return handles.includes(currentHandle);
      });

    const blockEl = document.querySelector('.claw-set-upgrade-block');
    const maxSets = parseInt(blockEl?.dataset.maxSets || 3);
    const toShow = matchedBundles.slice(0, maxSets);

    if (toShow.length > 0) {
      const container = document.querySelector('.claw-set-upgrade-block__sets');
      if (container) {
        container.innerHTML = toShow.map(buildTileHTML).join('');
        initSmoothClose(container);
        initATC(container);
      }
      if (blockEl) blockEl.style.display = '';
    } else {
      if (blockEl) blockEl.style.display = 'none';
    }
  } catch (err) {
    console.warn('[claw-set-upgrade] Storefront API error, falling back to Liquid render:', err);
    // Liquid fallback already rendered — init handlers on it
    const container = document.querySelector('.claw-set-upgrade-block__sets');
    if (container) {
      initSmoothClose(container);
      initATC(container);
    }
  }
})();
